import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

/**
 * Android-only native sharing of attachments.
 *
 * The Capacitor Share plugin needs `file://` URLs to share local files. We materialise each
 * attachment (data URL, File, or remote URL) into the app **Cache** directory and resolve it to a
 * `file://` URI via `Filesystem.getUri`. Cache files are exposed by Capacitor's FileProvider by
 * default on Android, so no `file_paths.xml` change is required.
 *
 * Remote files are fetched in-app (fetch -> Blob -> base64 -> writeFile) rather than using the
 * deprecated `Filesystem.downloadFile`.
 */

export type ShareItem =
  // Pending images: `base64` is already a `data:...;base64,...` URL.
  | { kind: 'dataUrl'; fileName: string; mimeType: string; dataUrl: string }
  // Pending videos/other files: real bytes live in the File object.
  | { kind: 'file'; fileName: string; file: File }
  // Existing (already-saved) attachments served from a remote backend URL.
  | { kind: 'remote'; fileName: string; url: string };

/** Strip the `data:<mime>;base64,` prefix, returning just the base64 payload. */
function stripDataUrlPrefix(dataUrl: string): string {
  const comma = dataUrl.indexOf(',');
  return comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
}

/** Read a Blob/File into a base64 string (no data-URL prefix). */
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(stripDataUrlPrefix(reader.result as string));
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/** Make a Cache-safe, collision-free file name while preserving the original extension. */
function uniqueCacheName(fileName: string, index: number): string {
  const safe = (fileName || `file-${index}`).replace(/[^\w.\-]+/g, '_');
  return `share_${Date.now()}_${index}_${safe}`;
}

/**
 * Convert a single ShareItem into a file in Directory.Cache.
 * Returns the relative cache `path` plus its `file://` `uri`, or null if it could not be made.
 */
async function materializeToCache(
  item: ShareItem,
  index: number
): Promise<{ path: string; uri: string } | null> {
  try {
    let base64: string;

    if (item.kind === 'dataUrl') {
      base64 = stripDataUrlPrefix(item.dataUrl);
    } else if (item.kind === 'file') {
      base64 = await blobToBase64(item.file);
    } else {
      // remote: fetch the asset and convert to base64.
      const res = await fetch(item.url);
      if (!res.ok) return null;
      const blob = await res.blob();
      base64 = await blobToBase64(blob);
    }

    const path = uniqueCacheName(item.fileName, index);
    await Filesystem.writeFile({ path, data: base64, directory: Directory.Cache });
    const { uri } = await Filesystem.getUri({ path, directory: Directory.Cache });
    return { path, uri };
  } catch {
    return null;
  }
}

/**
 * Materialise the given attachments to cache and open the native Android share sheet.
 * Throws if nothing could be shared. The temp cache files are best-effort cleaned up afterwards.
 */
export async function shareAttachments(items: ShareItem[]): Promise<void> {
  const written: { path: string; uri: string }[] = [];

  for (let i = 0; i < items.length; i++) {
    const result = await materializeToCache(items[i], i);
    if (result) written.push(result);
  }

  if (written.length === 0) {
    throw new Error('No shareable files could be prepared');
  }

  try {
    // `files` must be file:// URLs (returned by Filesystem.getUri above).
    await Share.share({ files: written.map((w) => w.uri), dialogTitle: 'שיתוף קבצים' });
  } finally {
    // Best-effort cleanup; Cache is auto-managed by the OS so failures are harmless.
    await Promise.all(
      written.map((w) =>
        Filesystem.deleteFile({ path: w.path, directory: Directory.Cache }).catch(() => undefined)
      )
    );
  }
}

/** Strip the `data:<mime>;base64,` prefix and decode the payload into a Blob. */
function dataUrlToBlob(dataUrl: string, mimeType: string): Blob {
  const base64 = stripDataUrlPrefix(dataUrl);
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mimeType });
}

/** Convert a single ShareItem into a File for the Web Share API / download fallback. */
async function shareItemToFile(item: ShareItem): Promise<File | null> {
  try {
    if (item.kind === 'file') {
      return item.file;
    }
    if (item.kind === 'dataUrl') {
      // Synchronous decode — keeps the click's user-activation alive for navigator.share.
      const blob = dataUrlToBlob(item.dataUrl, item.mimeType);
      return new File([blob], item.fileName, { type: item.mimeType });
    }
    // remote: fetch the asset (may be blocked by backend CORS).
    const res = await fetch(item.url);
    if (!res.ok) return null;
    const blob = await res.blob();
    return new File([blob], item.fileName, { type: blob.type || 'application/octet-stream' });
  } catch {
    return null;
  }
}

/** Download a single prepared File via a temporary object-URL anchor. */
function downloadFile(file: File): void {
  const objectUrl = URL.createObjectURL(file);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = file.name;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(objectUrl);
}

/**
 * Web (non-native) sharing of attachments.
 *
 * Materialises the items into in-memory `File` objects and opens the browser Web Share API
 * (`navigator.share({ files })`) when supported. Where file-sharing is unsupported (e.g. Firefox
 * desktop) it falls back to downloading each file. Throws if nothing could be prepared so the
 * caller's existing error handling fires.
 */
export async function shareAttachmentsWeb(items: ShareItem[]): Promise<void> {
  const files: File[] = [];
  for (const item of items) {
    const file = await shareItemToFile(item);
    if (file) files.push(file);
  }

  if (files.length === 0) {
    throw new Error('No shareable files could be prepared');
  }

  const nav = navigator as Navigator & { canShare?: (data?: ShareData) => boolean };
  if (typeof nav.share === 'function' && nav.canShare?.({ files })) {
    await nav.share({ files, title: 'שיתוף קבצים' });
    return;
  }

  // Fallback: object-URL downloads (the files are already in memory).
  files.forEach(downloadFile);
}
